import React, { useCallback, useMemo, useState } from "react";
import GlobalBadge from "../../../../global/GlobalBadge";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../global/directory";
import {
  TRACKING_EQUIPMENT_COLUMN_DEFS,
  TRACKING_EQUIPMENT_VISIBILITY_KEY,
  DONOR_DIRECTORY_PAGE_SIZE,
  buildValueOptionsFromRows,
  createInitialColumnVisibility,
  defaultDirectoryFilters,
  filterDirectoryRows,
  paginateList,
  trackingEquipmentCellValue,
  trackingEquipmentSearchableText,
} from "../../utils/donorPortalDirectoryHelpers";
import { formatTrackingAmount } from "../../utils/donationTrackingFormatters";

export default function DonationTrackingEquipmentDirectorySection({
  loading,
  equipment,
  equipmentSummary,
}) {
  const rows = Array.isArray(equipment) ? equipment : [];
  const totalEquipment = Number(equipmentSummary?.total_equipment || 0);

  const [filters, setFilters] = useState(defaultDirectoryFilters);
  const [page, setPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    createInitialColumnVisibility(
      TRACKING_EQUIPMENT_VISIBILITY_KEY,
      TRACKING_EQUIPMENT_COLUMN_DEFS
    )
  );

  const filtered = useMemo(
    () =>
      filterDirectoryRows(rows, filters, {
        getSearchableText: trackingEquipmentSearchableText,
        getColumnValue: trackingEquipmentCellValue,
      }),
    [rows, filters]
  );

  const { page: safePage, totalPages, total, slice } = useMemo(
    () => paginateList(filtered, page, DONOR_DIRECTORY_PAGE_SIZE),
    [filtered, page]
  );

  const valueFilterOptions = useMemo(() => {
    const col = filters.column;
    if (!col) return [];
    return buildValueOptionsFromRows(rows, col, trackingEquipmentCellValue);
  }, [filters.column, rows]);

  const visibleDataColumns = useMemo(
    () => TRACKING_EQUIPMENT_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility]
  );

  const tableHeaders = useMemo(
    () => visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
    [visibleDataColumns]
  );

  const getExportData = useCallback(() => {
    const headers = visibleDataColumns.map((c) => c.label);
    const dataRows = filtered.map((row) =>
      visibleDataColumns.map((c) => trackingEquipmentCellValue(c.id, row))
    );
    return { headers, rows: dataRows };
  }, [filtered, visibleDataColumns]);

  if (!loading && totalEquipment === 0) {
    return null;
  }

  const showRows = !loading && slice.length > 0;
  const verified = Number(equipmentSummary?.verified_equipment || 0);
  const pending = Number(equipmentSummary?.pending_equipment || 0);

  return (
    <div className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Equipment gifts</h3>
        <p className="user-builder-panel__hint">
          {verified} verified · {pending} pending · Est. value{" "}
          {formatTrackingAmount(equipmentSummary?.total_estimated_value)}
        </p>
      </div>

      <GlobalDirectoryShell
        filters={
          <GlobalDirectoryFiltersSection
            disabled={loading}
            searchLabel="Search"
            searchPlaceholder="Code, item, category, status…"
            searchValue={filters.search}
            onSearchChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(1);
            }}
            columnLabel="Column"
            columnValue={filters.column}
            onColumnChange={(v) => {
              setFilters((f) => ({ ...f, column: v, value: "" }));
              setPage(1);
            }}
            gridColumnDefs={TRACKING_EQUIPMENT_COLUMN_DEFS}
            valueLabel="Value"
            valueValue={filters.value}
            onValueChange={(v) => {
              setFilters((f) => ({ ...f, value: v }));
              setPage(1);
            }}
            valueOptions={valueFilterOptions}
            onClear={() => {
              setFilters(defaultDirectoryFilters());
              setPage(1);
            }}
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={TRACKING_EQUIPMENT_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={TRACKING_EQUIPMENT_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getExportData}
                filenameBase="donation_tracking_equipment"
              />
            }
          />
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading}
            loadingMessage="Loading equipment…"
            headers={tableHeaders}
            isEmpty={!loading && filtered.length === 0}
            emptyMessage="No equipment donations recorded."
            tableAriaLabel="Equipment donation tracking directory"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {showRows
              ? slice.map((row) => {
                  const verifiedRow = Number(row.donation_verified) === 1;
                  return (
                    <tr key={row.id}>
                      {visibleDataColumns.map((col) => (
                        <td key={col.id}>
                          {col.id === "status" ? (
                            <GlobalBadge
                              active={verifiedRow}
                              activeText="Verified"
                              inactiveText="Pending"
                            />
                          ) : (
                            trackingEquipmentCellValue(col.id, row)
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              : null}
          </GlobalDirectoryGridSection>
        }
        pagination={
          !loading && filtered.length > 0 ? (
            <GlobalDirectoryPaginationSection
              page={safePage}
              totalPages={totalPages}
              total={total}
              pageSize={DONOR_DIRECTORY_PAGE_SIZE}
              onPageChange={setPage}
              label="Equipment donations pagination"
            />
          ) : null
        }
      />
    </div>
  );
}
